import React, { useEffect, useMemo, useState } from 'react';
import { DOTS, usePagination } from './usePagination';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';

const Pagination = ({ setCurrentData, data, currentData, currentPage, setCurrentPage }) => {
  const totalCount = data?.length || 0;
  const [pageSize, setPageSize] = useState(10);

  const PAGE_OPTIONS = [
    { value: 5, label: '5' },
    { value: 10, label: '10' },
    { value: 20, label: '20' },
    { value: 50, label: '50' },
    { value: 100, label: '100' },
  ];

  const paginationRange = usePagination({ currentPage, totalCount, pageSize });

  const currentTableData = useMemo(() => {
    if (!data) return [];
    const firstPageIndex = (currentPage - 1) * pageSize;
    const lastPageIndex = firstPageIndex + pageSize;
    return data.slice(firstPageIndex, lastPageIndex);
  }, [currentPage, data, pageSize]);

  useEffect(() => {
    setCurrentData(currentTableData);
  }, [currentTableData, setCurrentData]);

  useEffect(() => {
    setCurrentPage(1);
  }, [pageSize, totalCount, setCurrentPage]);

  const handlePageChange = (page) => setCurrentPage(page);

  const lastPage = paginationRange ? paginationRange[paginationRange.length - 1] : 0;
  const numFirst = (currentPage - 1) * pageSize + 1;
  const numLast = Math.min(numFirst + currentData.length - 1, totalCount);

  return (
    <div className="pagination-container">
      <div className="pagination-info">{renderEntriesInfo(currentData, numFirst, numLast, totalCount)}</div>
      <div className="pagination-controls">
        <div className="entries-select">
          <label htmlFor="entriesSelect" className="form-label">
            Show
          </label>
          <select
            id="entriesSelect"
            className="form-select"
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
          >
            {PAGE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <span>Entries</span>
        </div>
        <nav aria-label="Table pagination">
          <ul className="pagination">
            {renderPrevButton(currentPage, handlePageChange)}
            {renderPaginationItems(paginationRange, currentPage, handlePageChange)}
            {renderNextButton(currentPage, lastPage, handlePageChange)}
          </ul>
        </nav>
      </div>
    </div>
  );
};

const renderEntriesInfo = (currentData, numFirst, numLast, totalCount) => {
  if (currentData.length === 0) return null;
  return (
    <p className="mb-0">
      Showing{' '}
      <span className="fw-bold">
        {numFirst}-{numLast}
      </span>{' '}
      of <span className="fw-bold">{totalCount}</span> Entries
    </p>
  );
};

const renderPrevButton = (currentPage, onPageChange) => (
  <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
    <button
      className="page-link"
      onClick={() => currentPage > 1 && onPageChange(currentPage - 1)}
      aria-label="Previous"
    >
      <ChevronLeft fontSize="small" />
    </button>
  </li>
);

const renderNextButton = (currentPage, lastPage, onPageChange) => (
  <li className={`page-item ${currentPage === lastPage ? 'disabled' : ''}`}>
    <button
      className="page-link"
      onClick={() => currentPage < lastPage && onPageChange(currentPage + 1)}
      aria-label="Next"
    >
      <ChevronRight fontSize="small" />
    </button>
  </li>
);

const renderPaginationItems = (paginationRange, currentPage, onPageChange) =>
  paginationRange?.map((pageNumber, key) => {
    if (pageNumber === DOTS) {
      return (
        <li key={key} className="page-item disabled">
          <span className="page-link">&#8230;</span>
        </li>
      );
    }
    return (
      <li key={key} className={`page-item ${pageNumber === currentPage ? 'active' : ''}`}>
        <button className="page-link" onClick={() => onPageChange(pageNumber)}>
          {pageNumber}
        </button>
      </li>
    );
  });

export default Pagination;
